/* src/stores/oddsStore.js */
import { defineStore } from 'pinia'
import _ from 'lodash'
import dayjs from 'dayjs'
import { isFixtureExpired } from '@/utils/expiry'

export const useOddsStore = defineStore('odds', {
  state: () => ({
    selectedOdds: (() => {
      try {
        const odds = JSON.parse(localStorage.getItem('selectedOdds')) || {}
        // Ensure each bet has required fields
        Object.values(odds).forEach((bet) => {
          if (!bet.status_name) {
            console.warn(
              `Loaded bet without 'status_name' for odd ID: ${bet.odd_id}. Setting default status.`
            )
            bet.status_name = 'unknown'
          }
          // Ensure stale flag exists for live bets
          if (bet.live === 'l' && bet.stale === undefined) {
            bet.stale = false
          }
        })
        return odds
      } catch (e) {
        console.error('Failed to parse selectedOdds from localStorage:', e)
        return {}
      }
    })(),
    codeNumber: localStorage.getItem('betSlipCodeNumber') || '',
    betId: localStorage.getItem('betId') || '',
    _debouncedSaveInstance: null,
    loadSharedTrigger: 0
  }),

  getters: {
    /**
     * Get a selected bet by match ID
     */
    getSelectedOdd: (state) => (matchId) => {
      return state.selectedOdds[matchId] || null
    },

    /**
     * Total number of bets in the betslip
     */
    totalBets: (state) => {
      return Object.keys(state.selectedOdds).length
    },

    /**
     * Array of all selected bets
     */
    selectedBets: (state) => {
      return Object.values(state.selectedOdds)
    },

    /**
     * Calculate total odds (product of all bet odds)
     */
    totalOdds: (state) => {
      const bets = Object.values(state.selectedOdds)
      if (bets.length === 0) return '0.00'
      const total = bets.reduce((acc, bet) => acc * parseFloat(bet.value), 1)
      return total.toFixed(2)
    },

    /**
     * Count of expired bets in the betslip
     */
    expiredBetsCount: (state) => {
      return Object.values(state.selectedOdds).filter(bet => isFixtureExpired(bet)).length
    },

    /**
     * Check if any bets are expired
     */
    hasExpiredBets: (state) => {
      return Object.values(state.selectedOdds).some(bet => isFixtureExpired(bet))
    },

    /**
     * Extract team names from a bet's match_name.
     * Expects match_name to be in the format "HomeTeam VS AwayTeam".
     */
    getTeamNames: (state) => (matchId) => {
      const bet = state.selectedOdds[matchId]
      if (bet && bet.match_name) {
        const teams = bet.match_name.split(' VS ')
        if (teams.length === 2) {
          return {
            homeTeam: teams[0].trim(),
            awayTeam: teams[1].trim()
          }
        }
      }
      return {
        homeTeam: '',
        awayTeam: ''
      }
    }
  },

  actions: {
    /**
     * Add or toggle a bet based on matchId and odd details.
     * @param {string|number} matchId - The ID of the match.
     * @param {Object} odd - The odd details.
     */
    addBet(matchId, odd) {
      if (!odd || !odd.odd_id) {
        console.error('Invalid odd or missing odd_id:', odd)
        return
      }

      // Ensure required fields exist
      if (!odd.status_name) {
        console.warn(
          `Adding bet without 'status_name' for odd ID: ${odd.odd_id}. Setting default status.`
        )
        odd.status_name = 'open'
      }

      // Ensure stale flag for live bets
      if (odd.live === 'l' && odd.stale === undefined) {
        odd.stale = false
      }

      // Ensure date field exists
      if (!odd.date) {
        console.warn(`Adding bet without 'date' for odd ID: ${odd.odd_id}`)
        odd.date = dayjs().format('YYYY-MM-DD HH:mm:ss')
      }

      // Toggle if already selected
      if (this.selectedOdds[matchId] && this.selectedOdds[matchId].odd_id === odd.odd_id) {
        delete this.selectedOdds[matchId]
        console.log(`Removed bet for match ID: ${matchId}`)
      } else {
        // Add/replace bet
        this.selectedOdds[matchId] = odd
        console.log(`Added bet for match ID: ${matchId}`)
      }

      this.getDebouncedSave()()
    },

    /**
     * Get the debounced save function with proper cancel method
     */
    getDebouncedSave() {
      if (!this._debouncedSaveInstance) {
        this._debouncedSaveInstance = _.debounce(() => {
          try {
            localStorage.setItem('selectedOdds', JSON.stringify(this.selectedOdds))
          } catch (e) {
            console.error('Failed to save selectedOdds to localStorage:', e)
          }
        }, 300)
      }
      return this._debouncedSaveInstance
    },

    /**
     * Immediately save selectedOdds to localStorage.
     */
    saveToLocalStorage() {
      try {
        localStorage.setItem('selectedOdds', JSON.stringify(this.selectedOdds))
      } catch (e) {
        console.error('Failed to save selectedOdds to localStorage:', e)
      }
    },

    /**
     * Clear all selected bets and remove them from localStorage.
     */
    clearAllBets() {
      console.log('🔴 Clearing all bets')

      // Cancel any pending debounced saves
      try {
        const debouncedSave = this.getDebouncedSave()
        if (debouncedSave && typeof debouncedSave.cancel === 'function') {
          debouncedSave.cancel()
        }
      } catch (e) {
        console.warn('Failed to cancel debounced saves:', e)
      }

      // Delete every bet for Vue reactivity
      Object.keys(this.selectedOdds).forEach((matchId) => {
        delete this.selectedOdds[matchId]
      })

      // Clear localStorage
      try {
        localStorage.removeItem('selectedOdds')
      } catch (e) {
        console.error('Failed to remove selectedOdds from localStorage:', e)
      }

      // Force immediate save of empty state
      this.saveToLocalStorage()
    },

    /**
     * Enhanced clear all bets function with race condition prevention
     */
    clearAllBetsEnhanced() {
      console.log('🔴 Enhanced: Clearing all bets')

      // Cancel any pending debounced saves to prevent race conditions
      try {
        const debouncedSave = this.getDebouncedSave()
        if (debouncedSave && typeof debouncedSave.cancel === 'function') {
          debouncedSave.cancel()
        }
      } catch (e) {
        console.warn('Failed to cancel debounced saves:', e)
      }

      // Completely replace the selectedOdds object to ensure reactivity
      this.selectedOdds = {}

      // Force clear localStorage
      try {
        localStorage.removeItem('selectedOdds')
        localStorage.setItem('selectedOdds', JSON.stringify({}))
      } catch (e) {
        console.error('Failed to clear localStorage:', e)
      }
    },

    /**
     * Remove a bet based on its odd_id.
     * @param {number|string} odd_id - The ID of the odd to remove.
     */
    removeBet(odd_id) {
      let removed = false
      for (const matchId in this.selectedOdds) {
        if (this.selectedOdds[matchId].odd_id === odd_id) {
          delete this.selectedOdds[matchId]
          console.log(`Removed odd ID: ${odd_id} from match ID: ${matchId}`)
          removed = true
          break
        }
      }

      if (!removed) {
        console.warn(`No bet found with odd ID: ${odd_id}`)
      }

      this.getDebouncedSave()()
    },

    /**
     * Remove all expired bets using the isFixtureExpired utility.
     * Returns the count of removed bets.
     * @returns {number} - Number of bets removed
     */
    removeExpiredBets() {
      console.log('🔍 Checking for expired bets...')

      const matchIdsToRemove = []

      // Identify all expired bets
      for (const matchId in this.selectedOdds) {
        const bet = this.selectedOdds[matchId]
        if (isFixtureExpired(bet)) {
          matchIdsToRemove.push(matchId)
          console.log(`📍 Marking match ${matchId} for removal (expired)`)
        }
      }

      // Remove all expired bets
      matchIdsToRemove.forEach(matchId => {
        delete this.selectedOdds[matchId]
        console.log(`🗑️ Removed expired bet for match ID: ${matchId}`)
      })

      // Persist changes
      if (matchIdsToRemove.length > 0) {
        this.getDebouncedSave()()
      }

      console.log(`✅ Removed ${matchIdsToRemove.length} expired bet(s)`)
      return matchIdsToRemove.length
    },

    /**
     * Remove a bet based on match_id.
     * @param {number|string} matchId - The ID of the match to remove.
     */
    removeBetByMatchId(matchId) {
      if (this.selectedOdds[matchId]) {
        delete this.selectedOdds[matchId]
        console.log(`Removed bet for match ID: ${matchId}`)
        this.getDebouncedSave()()
      } else {
        console.warn(`No bet found for match ID: ${matchId}`)
      }
    },

    /**
     * Update a bet's stale flag (for live bets).
     * @param {string|number} matchId - The ID of the match.
     * @param {boolean} isStale - Whether the bet is stale.
     */
    updateBetStaleStatus(matchId, isStale) {
      if (!this.selectedOdds[matchId]) {
        console.warn(`Cannot update stale status: No bet found for match ID: ${matchId}`)
        return
      }

      this.$patch((state) => {
        const bet = state.selectedOdds[matchId]
        if (bet.live === 'l') {
          bet.stale = isStale
          console.log(`Updated stale status for match ${matchId}: ${isStale}`)
        }
      })

      this.getDebouncedSave()()
    },

    /**
     * Update bet odds with new details.
     * @param {string|number} matchId - The ID of the match.
     * @param {number} newOdds - The new odds value.
     * @param {string} [newOutcomeName] - The new outcome name.
     * @param {string} [newAlias] - The new alias.
     * @param {string} [newStatusName] - The new status name.
     */
    updateBetOdds(matchId, newOdds, newOutcomeName, newAlias, newStatusName) {
      if (!this.selectedOdds[matchId]) {
        console.warn(`Cannot update odds: No bet found for match ID: ${matchId}`)
        return
      }

      const oldOdds = this.selectedOdds[matchId].value

      this.$patch((state) => {
        const existingBet = state.selectedOdds[matchId]

        // Update odds value
        existingBet.value = newOdds

        // Mark if odds changed
        if (parseFloat(oldOdds) !== parseFloat(newOdds)) {
          existingBet.oddsChanged = true
        }

        // Update other fields if provided
        if (newOutcomeName) existingBet.outcome_name = newOutcomeName
        if (newAlias) existingBet.alias = newAlias
        if (newStatusName) existingBet.status_name = newStatusName
      })

      console.log(`Updated odds for match ${matchId}: ${oldOdds} → ${newOdds}`)
      this.getDebouncedSave()()
    },

    /**
     * Calculate taxes and winnings based on stake and total odds.
     * @param {number} stake - The amount staked.
     * @param {number} totalOdds - The cumulative odds.
     * @returns {Object} - Tax and payout details.
     */
    calculateTaxesAndWinnings(stake, totalOdds) {
      const EXCISE_TAX_PERCENT = 15
      const WITHHOLDING_TAX_PERCENT = 20
      const MAX_PAYOUT = 1_000_000

      // 1) Excise tax (inclusive rate)
      const rawWagerTax = stake - (stake * 100) / (100 + EXCISE_TAX_PERCENT)
      const wagerTax = parseFloat(rawWagerTax.toFixed(4))
      const stakeAfterTax = parseFloat((stake - wagerTax).toFixed(4))

      // 2) Gross winning
      const winning = parseFloat((stakeAfterTax * totalOdds).toFixed(2))

      // 3) Withholding on profit (inclusive rate)
      const rawProfit = winning - stakeAfterTax
      const rawWithholdingTax = rawProfit - (rawProfit * 100) / (100 + WITHHOLDING_TAX_PERCENT)
      const withholdingTax = parseFloat(rawWithholdingTax.toFixed(4))

      // 4) Final payout
      const possibleWin = parseFloat((winning - withholdingTax).toFixed(4))
      const payout = Math.min(possibleWin, MAX_PAYOUT)

      return {
        wagerTax,
        stakeAfterTax,
        withholdingTax,
        payout,
        winning
      }
    },

    /**
     * Set the bet slip code number and persist it to localStorage.
     * @param {string} codeNumber - The code number to set.
     */
    setCodeNumber(codeNumber) {
      this.codeNumber = codeNumber
      try {
        localStorage.setItem('betSlipCodeNumber', codeNumber)
      } catch (e) {
        console.error('Failed to save code number to localStorage:', e)
      }
    },

    /**
     * Clear the bet slip code number and remove it from localStorage.
     */
    clearCodeNumber() {
      this.codeNumber = ''
      try {
        localStorage.removeItem('betSlipCodeNumber')
      } catch (e) {
        console.error('Failed to remove code number from localStorage:', e)
      }
    },

    /**
     * Persist the last-placed Bet ID.
     * @param {string} id - The bet reference returned by the API.
     */
    setBetId(id) {
      this.betId = id
      try {
        localStorage.setItem('betId', id)
      } catch (e) {
        console.error('Failed to save bet ID to localStorage:', e)
      }
    },

    /**
     * Clear the persisted Bet ID.
     */
    clearBetId() {
      this.betId = ''
      try {
        localStorage.removeItem('betId')
      } catch (e) {
        console.error('Failed to remove bet ID from localStorage:', e)
      }
    },

    /**
     * Trigger a shared bet load (increments trigger for watcher).
     */
    triggerLoadShared() {
      this.loadSharedTrigger++
      console.log(`Triggered shared bet load: ${this.loadSharedTrigger}`)
    },

    /**
     * Bulk update multiple bets from WebSocket data
     * @param {Array} updates - Array of bet updates from WebSocket
     */
    bulkUpdateBets(updates) {
      if (!Array.isArray(updates) || updates.length === 0) return

      let hasChanges = false

      updates.forEach(update => {
        const { matchId, odds, outcomeName, alias, statusName, stale } = update

        if (this.selectedOdds[matchId]) {
          const bet = this.selectedOdds[matchId]

          // Update fields if they've changed
          if (odds !== undefined && bet.value !== odds) {
            bet.value = odds
            bet.oddsChanged = true
            hasChanges = true
          }

          if (outcomeName && bet.outcome_name !== outcomeName) {
            bet.outcome_name = outcomeName
            hasChanges = true
          }

          if (alias && bet.alias !== alias) {
            bet.alias = alias
            hasChanges = true
          }

          if (statusName && bet.status_name !== statusName) {
            bet.status_name = statusName
            hasChanges = true
          }

          if (stale !== undefined && bet.stale !== stale) {
            bet.stale = stale
            hasChanges = true
          }
        }
      })

      if (hasChanges) {
        this.getDebouncedSave()()
      }
    },

    /**
     * Reset odds changed flags for all bets
     */
    resetOddsChangedFlags() {
      let hasChanges = false

      Object.values(this.selectedOdds).forEach(bet => {
        if (bet.oddsChanged) {
          bet.oddsChanged = false
          hasChanges = true
        }
      })

      if (hasChanges) {
        this.getDebouncedSave()()
      }
    }
  }
})
